import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Formik } from 'formik';
import { formatPhoneNumber } from '../Shared/utils';
import { FormInput } from '../Shared/components/FormInput';
import { Paginate } from '../Shared/components/Paginate';
import {
  INDEPENDENT_AGENT_STATES,
  STATE_OPTIONS,
} from '../Shared/constants/states';
import { useFilters } from '../Shared/components/Table/hooks';
import { Button, Asset, FilterIcon } from '@clearcover/atlas';
import {
  postSearch,
  QueriesType,
  QuoteSearchResults as QuoteSearchResultsType,
} from '../Shared/services/Reznor/PostSearch';
import { postLead } from '../Shared/services/Reznor/PostLead';
import { useAuth0 } from '../Auth';
import { QuoteSearchResults } from './components/QuoteSearchResults';
import { AnimatedScreen } from '../Shared/components/AnimatedScreen';
import { Can } from '../Shared/utils/auth';
import { createQuotes } from '../Admin/authPolicies';
import styles from './styles.module.css';
import { RadioGroup } from '../Shared/components/RadioGroup';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { useFullstory } from 'Shared/hooks';

const searchFilters = [
  {
    field: 'quote_number',
    getFilterPayload: (text: Array<string>) => text[0],
  },
  {
    field: 'first_name',
    getFilterPayload: (text: Array<string>) => text[0],
  },
  {
    field: 'last_name',
    getFilterPayload: (text: Array<string>) => text[0],
  },
  {
    field: 'email',
    getFilterPayload: (text: Array<string>) => text[0],
  },
  {
    field: 'phone_number',
    getFilterPayload: (text: Array<string>) => text[0].split('-').join(''),
  },
];

export const useQuoteSearch = () => {
  const { getToken } = useAuth0();
  const [quoteResults, setQuoteResults] = useState<
    QuoteSearchResultsType['agentQuotes']
  >([]);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);

  const { fsCommands } = useFullstory();

  const fetchResults = useCallback(
    async (queries?: QueriesType) => {
      setIsLoading(true);
      setIsError(false);

      const token = await getToken();
      const response = await postSearch(token, queries);

      if (!response || response.status !== 200) {
        setIsError(true);
      } else {
        setQuoteResults(response.data.agentQuotes);
        setResultsTotal(response.data.total);
      }

      setIsLoading(false);
    },
    [getToken]
  );

  const createQuote = useCallback(async () => {
    setIsCreatingQuote(true);

    const token = await getToken();
    const response = await postLead(token);

    if (response && response.status >= 200 && response.status < 300) {
      const { leadMetadata, ...quoteInfo } = response.data;

      fsCommands.updatePersistedFullstoryMetadata({
        new_campaign_name: leadMetadata.campaignName || 'none',
        new_lead_identifier: leadMetadata.leadIdentifier || 'none',
        new_origination_channel: 'agent',
      });
      setQuoteResults([...quoteResults, quoteInfo]);
      setResultsTotal(resultsTotal + 1);
      window.open(quoteInfo.resumeLink, '_blank');
    } else {
      setIsError(true);
    }

    setIsCreatingQuote(false);
  }, [getToken, quoteResults, resultsTotal, fsCommands]);

  return {
    isCreatingQuote,
    createQuote,
    isLoading,
    isError,
    quoteResults,
    resultsTotal,
    fetchResults,
  };
};

const QuoteSearch: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const { user } = useAuth0();
  const quoteSearchProps = useQuoteSearch();
  const {
    fetchResults,
    isCreatingQuote,
    createQuote,
    resultsTotal,
    isLoading,
  } = quoteSearchProps;

  const {
    activeFilters,
    hasActiveFilters,
    modifyMultipleFilters,
    addValueToFilter,
    removeValueFromFilter,
    clearAllFilters,
  } = useFilters({});

  const [filterOwnQuotes, changeFilterOwnQuotes] = useState(false);

  const handleSearchSubmit = (data: { [field: string]: string }) => {
    modifyMultipleFilters(
      Object.keys(data).reduce((acc, key) => {
        return { ...acc, [key]: [data[key]] };
      }, {})
    );
  };

  const multiSelectFilters = useMemo(
    () => [
      {
        fieldName: 'State',
        field: 'policy_state',
        availableFilters: INDEPENDENT_AGENT_STATES.map(state => ({
          display: `${state} - ${STATE_OPTIONS[state]?.toUpperCase()}`,
          value: state,
        })),
        getFilterPayload: (text: Array<string>) => text,
      },
    ],
    []
  );

  const buildFetchResultsPayload = useCallback(
    (activeFilters: { [field: string]: string[] }): QueriesType => {
      return Object.keys(activeFilters).reduce((filters, filter) => {
        const activeFilter = [...multiSelectFilters, ...searchFilters].find(
          f => f.field === filter
        );

        if (
          activeFilter &&
          activeFilter.getFilterPayload(activeFilters[filter]).length
        ) {
          return {
            ...filters,
            [filter]: activeFilter.getFilterPayload(activeFilters[filter]),
          };
        } else {
          return filters;
        }
      }, {});
    },
    [multiSelectFilters]
  );

  useEffect(() => {
    if (Object.keys(activeFilters).length === 0) {
      fetchResults({
        originating_portal_user_ids:
          filterOwnQuotes && user ? [user.id] : undefined,
      });
    } else {
      fetchResults({
        ...buildFetchResultsPayload(activeFilters),
        originating_portal_user_ids:
          filterOwnQuotes && user ? [user.id] : undefined,
      });
    }
  }, [
    activeFilters,
    filterOwnQuotes,
    fetchResults,
    user,
    buildFetchResultsPayload,
  ]);

  return (
    <AnimatedScreen testId="quote-search" className={styles.main}>
      <div className={styles.header}>
        <h1>Quotes</h1>
        <Can
          grants={user!.effectiveGrants}
          policy={createQuotes}
          yes={() => (
            <Button
              isSubmitting={isCreatingQuote}
              variant="primary"
              onClick={createQuote}
              data-testid="create-quote-button"
            >
              Create Quote
            </Button>
          )}
        />
      </div>
      <Formik
        enableReinitialize
        initialValues={{
          quote_number: activeFilters.quote_number?.[0] || '',
          first_name: activeFilters.first_name?.[0] || '',
          last_name: activeFilters.last_name?.[0] || '',
          email: activeFilters.email?.[0] || '',
          phone_number: activeFilters.phone_number?.[0] || '',
        }}
        onSubmit={handleSearchSubmit}
      >
        {({ handleSubmit, values, handleChange }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={styles.search}
              data-testid="form"
            >
              <FormInput
                name="quote_number"
                value={values.quote_number}
                label="Search quote number"
                variant="search"
                onChange={handleChange}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                name="first_name"
                value={values.first_name}
                label="Search first name"
                variant="search"
                onChange={handleChange}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                name="last_name"
                value={values.last_name}
                label="Search last name"
                variant="search"
                onChange={handleChange}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                name="email"
                value={values.email}
                label="Search email address"
                variant="search"
                onChange={handleChange}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                name="phone_number"
                value={formatPhoneNumber(values.phone_number)}
                label="Search phone number"
                variant="search"
                onChange={handleChange}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <button type="submit" className={styles.submit} />
            </form>
          );
        }}
      </Formik>
      <div className={styles.filters}>
        <span className={styles.filterHeader}>
          Filters
          <Asset
            className={styles.filterIcon}
            icon={FilterIcon}
            height={16}
            width={16}
            description="Filter"
          />
        </span>
        {multiSelectFilters.map(f => {
          const activeFiltersForField = activeFilters[f.field] || [];

          return (
            <FormInput
              key={f.field}
              variant="multiselect"
              items={f.availableFilters.map(({ display, value }) => ({
                key: value,
                label: display,
                selected: activeFiltersForField.includes(value),
              }))}
              label={f.fieldName}
              onChangeSelection={item => {
                if (item) {
                  item.selected
                    ? removeValueFromFilter(f.field, item.key)
                    : addValueToFilter(f.field, item.key);
                }
              }}
            />
          );
        })}
        <ClearFilterButton
          onClick={clearAllFilters}
          data-testid="clear-filters"
          disabled={!hasActiveFilters}
        >
          Clear Filters And Search
        </ClearFilterButton>
        <RadioGroup
          className={styles.filterOwnQuotes}
          name="quoteFilter"
          variant="inline"
          options={[
            { label: 'My Quotes', value: 'USER' },
            { label: 'All Quotes', value: 'AGENCY' },
          ]}
          value={filterOwnQuotes ? 'USER' : 'AGENCY'}
          onChange={(value: string) => changeFilterOwnQuotes(value === 'USER')}
        />
      </div>
      <QuoteSearchResults {...quoteSearchProps} />
      {!!quoteSearchProps.resultsTotal && !isLoading && (
        <Paginate
          limit={20}
          total={resultsTotal}
          offset={offset}
          onOffsetChange={(limit, offset) => {
            setOffset(offset);
            fetchResults({
              ...buildFetchResultsPayload(activeFilters),
              originating_portal_user_ids:
                filterOwnQuotes && user ? [user.id] : undefined,
              page: offset / limit + 1,
            });
          }}
        />
      )}
    </AnimatedScreen>
  );
};

export { QuoteSearch };
